import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ForwardIcon from '@mui/icons-material/Forward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import NotesIcon from '@mui/icons-material/Notes';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { signedMoney } from './drawerFormat.js';
import { billingBrandLabel } from './brandDisplay.js';
import ExpandableLineItem from './ExpandableLineItem.jsx';
import { resolvedFileHealthFindings } from './fileHealthResolution.js';
import {
  useBulkReviewBillingReconciliationFileHealth,
  useReviewBillingReconciliationFileHealth,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import {
  fileHealthCodeLabel,
  fileHealthNotes,
  fileHealthOpenState,
  fileHealthSummaryText,
} from './fileHealthDisplay.js';
import ReviewNoteDialog from './ReviewNoteDialog.jsx';

const DRIVER_ACRONYMS = new Set(['BAC', 'CPQ', 'DPE', 'GM', 'SEC', 'SF']);
const humanizeDriver = (value) => String(value || '')
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((part) => {
    const upper = part.toUpperCase();
    if (DRIVER_ACRONYMS.has(upper)) return upper;
    const lower = part.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  })
  .join(' ');

const enrollmentStatusLabel = (row = {}) => {
  const value = row.dpeEnrollmentStatusCd
    || row.dpe_enrollment_status_cd
    || row.enrollmentStatusCd
    || row.enrollment_status_cd
    || row.enrollmentStatus
    || row.currentEnrollmentStatus;
  return humanizeDriver(value);
};

export const PairwiseDeltas = ({ deltas = {} }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6, fontSize: 10.5, display: 'block', mb: 1 }}
    >
      Pairwise deltas
    </Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}>
      {[
        { label: 'GM - CPQ', value: deltas.gmVsCpq, emphasis: true },
        { label: 'GM - DPE', value: deltas.gmVsDpe },
        { label: 'CPQ - DPE', value: deltas.cpqVsDpe },
      ].map((delta) => {
        const value = Number(delta.value) || 0;
        const isMatch = Math.abs(value) <= 1;
        const color = isMatch ? 'success.main' : value > 0 ? 'warning.main' : 'error.main';
        return (
          <Box key={delta.label} sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.3 }}>
                {delta.label}
              </Typography>
            </Stack>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color, letterSpacing: -0.3, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
            >
              {signedMoney(value)}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  </Paper>
);

export const DrawerHeader = ({
  row,
  review,
  isReviewed,
  drawerMode = 'wide',
  onDrawerModeChange = null,
  evidenceStatus = null,
  peopleSoftId = '',
  onClose,
}) => {
  const driverLabel = humanizeDriver(row.varianceDriver?.label || row.varianceDriver?.code || row.autoDriver || row.reviewDriver);
  const varianceDriver = row.varianceDriver?.detail
    ? `${driverLabel} - ${row.varianceDriver.detail}`
    : driverLabel;
  const currentEnrollmentStatus = enrollmentStatusLabel(row);
  const displayBrand = billingBrandLabel(row);
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.15 }} noWrap>
            {row.dealerDisplayName || row.dealerName || '--'}
          </Typography>
          {evidenceStatus ? (
            <Tooltip title={evidenceStatus.message}>
              <IconButton
                size="small"
                aria-label="Billing evidence status"
                sx={{ width: 26, height: 26, color: evidenceStatus.color }}
              >
                {evidenceStatus.icon}
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.35, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>
            BAC {row.bac}
          </Typography>
          {peopleSoftId ? (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Tooltip title="PeopleSoft ID">
                <Typography
                  component="span"
                  tabIndex={0}
                  variant="caption"
                  aria-label={`PeopleSoft ID ${peopleSoftId}`}
                  sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', fontWeight: 700, cursor: 'help' }}
                >
                  {peopleSoftId}
                </Typography>
              </Tooltip>
            </>
          ) : null}
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {displayBrand || 'Unknown brand'}
          </Typography>
          {currentEnrollmentStatus ? (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {currentEnrollmentStatus}
              </Typography>
            </>
          ) : null}
          {varianceDriver ? (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {varianceDriver}
              </Typography>
            </>
          ) : null}
          {isReviewed && (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                Reviewed
              </Typography>
            </>
          )}
          {review?.inheritedFromRound && (
            <Tooltip
              title={
                review.inheritedResolved
                  ? `This variance was investigated in Round ${review.inheritedFromRound}${review.inheritedFromDate ? ` (${new Date(review.inheritedFromDate).toLocaleDateString()})` : ''} and has resolved this round. History preserved for reference.`
                  : `Review carried over from Round ${review.inheritedFromRound}${review.inheritedFromDate ? ` (${new Date(review.inheritedFromDate).toLocaleDateString()})` : ''}. Same variance reappeared; prior notes are preserved below.`
              }
            >
              <Chip
                size="small"
                icon={<ForwardIcon sx={{ fontSize: 14 }} />}
                label={review.inheritedResolved ? `Resolved from Round ${review.inheritedFromRound}` : `Inherited from Round ${review.inheritedFromRound}`}
                variant="outlined"
                sx={{
                  height: 22,
                  color: review.inheritedResolved ? 'success.main' : 'info.main',
                  borderColor: review.inheritedResolved ? 'success.main' : 'info.main',
                  fontWeight: 600,
                }}
              />
            </Tooltip>
          )}
          {row.accountMatchSummary?.status === 'multiple_matches' && (
            <Tooltip title={`${row.accountMatchSummary.count || 2} Salesforce accounts`}>
              <Chip
                size="small"
                icon={<CloudQueueIcon sx={{ fontSize: 14 }} />}
                label={row.accountMatchSummary.count || 2}
                color="info"
                variant="outlined"
                sx={{ height: 22, fontWeight: 800 }}
              />
            </Tooltip>
          )}
        </Stack>
      </Box>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {onDrawerModeChange ? (
          <Tooltip title={drawerMode === 'fullscreen' ? 'Exit full screen' : 'Open full screen'}>
            <IconButton
              onClick={() => onDrawerModeChange(drawerMode === 'fullscreen' ? 'wide' : 'fullscreen')}
              aria-label={drawerMode === 'fullscreen' ? 'Exit full screen drawer' : 'Open full screen drawer'}
            >
              {drawerMode === 'fullscreen' ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        ) : null}
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export const DrawerNavigationControls = ({ navigation = null }) => {
  if (!navigation || !navigation.total) return null;
  const canMove = navigation.total > 1;
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        px: 1,
        py: 0.75,
        bgcolor: 'background.default',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Tooltip title={canMove ? `Previous BAC ${navigation.previousBac || ''}` : 'No previous variance'}>
          <span>
            <IconButton
              size="small"
              onClick={navigation.onPrevious || undefined}
              disabled={!canMove || !navigation.onPrevious}
              aria-label="Previous variance"
            >
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Stack spacing={0.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 1 }}>
            Variance {navigation.position || 0} of {navigation.total}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
            N / P or arrow keys
          </Typography>
        </Stack>
        <Tooltip title={canMove ? `Next BAC ${navigation.nextBac || ''}` : 'No next variance'}>
          <span>
            <IconButton
              size="small"
              onClick={navigation.onNext || undefined}
              disabled={!canMove || !navigation.onNext}
              aria-label="Next variance"
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

export const evidenceStatusConfig = ({ mockMode, query, row, runId }) => {
  if (mockMode) return null;
  if (!query) return null;
  if (query.isError) {
    return {
      color: 'error.main',
      icon: <ErrorOutlineIcon fontSize="small" />,
      message: `Could not load stored billing evidence for BAC ${row.bac}. The totals are still shown from the variance record.`,
    };
  }
  if ((query.isLoading || query.isFetching) && !query.data) {
    return {
      color: 'info.main',
      icon: <InfoOutlinedIcon fontSize="small" />,
      message: `Loading GM, CPQ, DPE, and AppSheet evidence for BAC ${row.bac}...`,
    };
  }
  const sourceErrors = query.data?.sourceErrors || {};
  const failedSources = Object.entries(sourceErrors).filter(([, error]) => error).map(([name]) => name);
  if (failedSources.length > 0) {
    return {
      color: 'warning.main',
      icon: <WarningAmberIcon fontSize="small" />,
      message: `Some evidence sources could not be loaded for BAC ${row.bac}: ${failedSources.join(', ')}. Available evidence is still shown.`,
    };
  }
  return {
    color: 'success.main',
    icon: <CheckCircleIcon fontSize="small" />,
    message: `Showing billing evidence for run ${runId}.`,
  };
};

export const EvidenceStatus = ({ mockMode, query, row, runId }) => {
  const status = evidenceStatusConfig({ mockMode, query, row, runId });
  if (!status) return null;
  return <Alert severity={status.color.split('.')[0]}>{status.message}</Alert>;
};

const rawRowForFinding = (finding = {}) => finding.rawRow || finding.raw_row || {};

const derivedRowFieldsForFinding = (finding = {}) => ({
  'Product Code': finding.productCode || finding.product_code || finding.normalizedProductCode || finding.normalized_product_code,
  'Product Selection': finding.productSelection || finding.product_selection,
  'Product Name': finding.productName || finding.product_name,
  'Product Brand': finding.productBrand || finding.product_brand,
  'Product Status': finding.productStatus || finding.product_status,
  'Dealer Cost': finding.dealerCost || finding.dealer_cost,
  Budget: finding.budget,
  'Effective Date': finding.effectiveDate || finding.effective_date,
  'Billing Start Date': finding.billingStartDate || finding.billing_start_date,
  'Billing End Date': finding.billingEndDate || finding.billing_end_date,
  'Activation Date': finding.activationDate || finding.activation_date,
  'Enrollment Date': finding.enrollmentDate || finding.enrollment_date,
});

const fileHealthDetails = (finding = {}) => {
  const rawRow = rawRowForFinding(finding);
  const notes = fileHealthNotes(finding);
  return {
    'Source notes': notes,
    File: String(finding.fileKind || 'file').toUpperCase(),
    'Row number': finding.rowNumber || '',
    BAC: finding.bac || '',
    Code: finding.code || '',
    Severity: finding.tier || '',
    'Review state': finding.reviewState || '',
    'Review memory': finding.reviewMemory?.reason || '',
    'Review memory source': finding.reviewMemory?.source || '',
    'Prior review month': finding.reviewMemory?.sourceServiceMonth || '',
    'Prior review actor': finding.reviewMemory?.reviewedBy || '',
    'Review note': finding.reviewNote || '',
    Expected: finding.expectedValue ?? '',
    Actual: finding.actualValue ?? '',
    ...Object.fromEntries(
      Object.entries(derivedRowFieldsForFinding(finding))
        .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
    ),
    ...Object.fromEntries(
      Object.entries(rawRow)
        .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
    ),
  };
};

const FileHealthFindingLine = ({ finding, onReview, reviewPending, defaultOpen = false }) => {
  const rowRef = useRef(null);
  const fileKind = String(finding.fileKind || 'file').toUpperCase();
  const code = finding.code || 'File health finding';
  const hasNotes = Boolean(fileHealthNotes(finding));
  const isOpen = fileHealthOpenState(finding);
  const severityIcon = finding.tier === 'error'
    ? <ErrorOutlineIcon fontSize="small" />
    : finding.tier === 'warning'
      ? <WarningAmberIcon fontSize="small" />
      : <InfoOutlinedIcon fontSize="small" />;
  const severityColor = !isOpen ? 'success.main' : finding.tier === 'error' ? 'error.main' : finding.tier === 'warning' ? 'warning.main' : 'info.main';
  useEffect(() => {
    if (!defaultOpen) return;
    rowRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [defaultOpen]);
  return (
    <Stack ref={rowRef} direction="row" spacing={0.5} alignItems="center">
      <Tooltip title={`${finding.tier || 'Info'} finding`}>
        <Box
          aria-label={`${finding.tier || 'info'} file health finding`}
          sx={{
            color: severityColor,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {severityIcon}
        </Box>
      </Tooltip>
      {hasNotes ? (
        <Tooltip title="Source row includes notes">
          <NotesIcon color="info" fontSize="small" />
        </Tooltip>
      ) : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <ExpandableLineItem
          dense
          primary={fileHealthCodeLabel(code)}
          secondary={`${fileKind} row ${finding.rowNumber || '--'}`}
          details={fileHealthDetails(finding)}
          defaultOpen={defaultOpen}
          meta={[
            { label: 'file', value: fileKind },
            { label: 'row', value: finding.rowNumber || '--' },
            { label: 'summary', value: fileHealthSummaryText(finding), mono: false },
          ]}
        />
      </Box>
      {isOpen ? (
        <Button
          size="small"
          variant="outlined"
          color="success"
          disabled={reviewPending}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onReview?.(finding);
          }}
        >
          Reviewed
        </Button>
      ) : null}
    </Stack>
  );
};

const sortFileHealthFindings = (findings = []) => [...findings].sort((left, right) => {
  const severityRank = { error: 0, warning: 1, info: 2 };
  const leftSeverity = severityRank[left.tier] ?? 3;
  const rightSeverity = severityRank[right.tier] ?? 3;
  if (leftSeverity !== rightSeverity) return leftSeverity - rightSeverity;
  const codeCompare = String(left.code || '').localeCompare(String(right.code || ''));
  if (codeCompare) return codeCompare;
  return Number(left.rowNumber || 0) - Number(right.rowNumber || 0);
});

export const FileHealthSummary = ({ query, varianceActions = [], context = null }) => {
  const review = useReviewBillingReconciliationFileHealth();
  const bulkReview = useBulkReviewBillingReconciliationFileHealth();
  const [reviewDialogFinding, setReviewDialogFinding] = useState(null);
  const [expanded, setExpanded] = useState(Boolean(context?.source === 'file_health'));
  const rawFindings = Array.isArray(query?.data) ? query.data : [];
  const findings = resolvedFileHealthFindings(rawFindings, varianceActions);
  if (query?.isLoading || query?.isFetching) return null;
  if (query?.isError) return <Alert severity="warning">File Health findings for this BAC could not be loaded.</Alert>;
  const focusFindingId = context?.source === 'file_health' ? context.findingId : null;
  const focusMatches = (finding) => Boolean(context?.source === 'file_health' && (
    (focusFindingId && finding.id === focusFindingId)
    || (context.findingFingerprint && finding.findingFingerprint === context.findingFingerprint)
    || (
      context.broadAlertKey
      && context.materialFactsHash
      && finding.broadAlertKey === context.broadAlertKey
      && finding.materialFactsHash === context.materialFactsHash
    )
  ));
  const openFindings = findings.filter(fileHealthOpenState);
  const focusedFinding = findings.find(focusMatches);
  const visibleFindings = focusedFinding && !openFindings.some((finding) => finding.id === focusedFinding.id)
    ? [...openFindings, focusedFinding]
    : openFindings;
  if (!visibleFindings.length) return null;
  const errors = visibleFindings.filter((finding) => finding.tier === 'error').length;
  const severity = errors ? 'error' : 'warning';
  const sortedFindings = sortFileHealthFindings(visibleFindings);
  const handleReview = (finding) => setReviewDialogFinding(finding);
  const reviewPayloadFor = (finding, reviewNote = '') => ({
    id: finding.id,
    runId: finding.runId,
    findingFingerprint: finding.findingFingerprint,
    broadAlertKey: finding.broadAlertKey,
    materialFactsHash: finding.materialFactsHash,
    state: 'addressed',
    reviewNote,
  });
  const submitReview = (reviewNote) => {
    const finding = reviewDialogFinding;
    if (!finding) return;
    review.mutate(reviewPayloadFor(finding, reviewNote));
    setReviewDialogFinding(null);
  };
  const reviewAll = () => {
    const runId = sortedFindings.find((finding) => finding.runId)?.runId;
    const bac = sortedFindings.find((finding) => finding.bac)?.bac;
    if (!runId || !bac || !openFindings.length) return;
    bulkReview.mutate({
      runId,
      bac,
      state: 'addressed',
      reviewNote: 'Bulk reviewed from BAC drawer.',
    });
  };
  return (
    <>
      <Accordion
        disableGutters
        expanded={expanded}
        onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
        variant="outlined"
        sx={{
          borderColor: severity === 'error' ? 'error.light' : 'warning.light',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              File Health for this BAC
            </Typography>
            <Chip size="small" label={`${openFindings.length} open`} color={severity} variant="outlined" />
            {focusedFinding && !fileHealthOpenState(focusedFinding) ? (
              <Chip size="small" label="focused addressed finding" color="success" variant="outlined" />
            ) : null}
            {errors > 0 && <Chip size="small" label={`${errors} error${errors === 1 ? '' : 's'}`} color="error" />}
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={review.isPending || bulkReview.isPending || !openFindings.length}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                reviewAll();
              }}
              sx={{ ml: 'auto', textTransform: 'none' }}
            >
              Review all
            </Button>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={0.5}>
            {sortedFindings.map((finding) => (
              <FileHealthFindingLine
                key={finding.id || `${finding.fileKind}-${finding.rowNumber}-${finding.code}`}
                finding={finding}
                onReview={handleReview}
                reviewPending={review.isPending || bulkReview.isPending}
                defaultOpen={focusMatches(finding)}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <ReviewNoteDialog
        open={Boolean(reviewDialogFinding)}
        title="Mark File Health Finding Addressed"
        label="Optional mitigation note"
        initialValue={reviewDialogFinding?.reviewNote || ''}
        confirmLabel="Mark Addressed"
        onClose={() => setReviewDialogFinding(null)}
        onConfirm={submitReview}
      />
    </>
  );
};

export const DrawerContextAlert = ({ context, onResolveAccount = null }) => {
  if (!context) return null;
  if (context.source === 'file_health' || context.source === 'lifecycle') return null;
  const severity = ['error', 'warning', 'info', 'success'].includes(context.severity) ? context.severity : 'info';
  const canResolveAccount = context.code === 'MISSING_SALESFORCE_ACCOUNT' && typeof onResolveAccount === 'function';
  return (
    <Alert
      severity={severity}
      action={canResolveAccount ? (
        <Button color="inherit" size="small" onClick={onResolveAccount}>
          Assign account
        </Button>
      ) : null}
    >
      <Stack spacing={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {context.title || context.code || 'Opened from billing review'}
        </Typography>
        {context.summary && (
          <Typography variant="body2">
            {context.summary}
          </Typography>
        )}
        {context.detail && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {context.detail}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
};
